import {
  Button,
  DotMenu,
  Label,
  typeSize,
  UserProfileImage,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { format, fromUnixTime } from "date-fns";
import { FaCalendar, FaEye, FaMapMarkerAlt, FaTrash } from "react-icons/fa";
import { MdRepeat } from "react-icons/md";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useConversationActions } from "./ConversationActions";
import {
  ConvoAndInstance,
  detailsHref,
  isCancelled,
  isCompleted,
  isUpcoming,
  playHref,
  recurrenceLabel,
} from "./conversationList";

const Row = styled.li<{ dim?: boolean }>`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey5};
  opacity: ${(p) => (p.dim ? 0.7 : 1)};

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 640px) {
    grid-template-columns: 48px minmax(0, 1fr);

    & > div:last-child {
      grid-column: 1 / -1;
      justify-content: flex-end;
    }
  }
`;

const DateBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  text-align: center;

  & > span:nth-child(1),
  & > span:nth-child(3) {
    font-size: ${typeSize("caption")};
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
    text-transform: uppercase;
  }

  & > span:nth-child(2) {
    font-size: ${typeSize("page")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
    line-height: 1.1;
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const TitleLine = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;

  & > a,
  & > strong {
    font-size: ${typeSize("title")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
    text-decoration: none;
  }

  & > a:hover {
    text-decoration: underline;
  }

  & > span {
    font-size: ${typeSize("small")};
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const Meta = styled.div`
  display: flex;
  gap: 6px 16px;
  align-items: center;
  flex-wrap: wrap;
  font-size: ${typeSize("small")};
  color: ${(p) => p.theme.colors.textMuted};

  & > span {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }

  & > span:first-child {
    color: ${(p) => p.theme.colors.body};
  }

  & svg {
    flex-shrink: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

export type ConversationRowProps = {
  convo: ConvoAndInstance;
};

export const ConversationRow = ({ convo }: ConversationRowProps) => {
  const [user] = useUser();
  const history = useHistory();
  const actions = useConversationActions(convo);
  const date = fromUnixTime(convo.scheduledDate);

  const isObserver = convo.observers.some((o) => o.id === user.id);
  const withManager = convo.directReport.id === user.id;
  const other = convo.manager.id === user.id ? convo.directReport : convo.manager;
  const repeat = recurrenceLabel(convo.recurrence);
  const upcoming = isUpcoming(convo);
  const cancelled = isCancelled(convo);
  const completed = isCompleted(convo);
  const agendaCount = convo.agenda?.length ?? 0;

  const status = completed ? (
    <Label size="small" color="positive">
      Completed
    </Label>
  ) : cancelled ? (
    <Label size="small">
      Cancelled
      {convo.cancellationReason ? `: ${convo.cancellationReason.text}` : ""}
    </Label>
  ) : !upcoming ? (
    <Label size="small" color="warning">
      Not marked complete
    </Label>
  ) : agendaCount === 0 ? (
    <Label size="small" color="warning">
      No agenda yet
    </Label>
  ) : (
    <Label size="small">
      {agendaCount} agenda {agendaCount === 1 ? "point" : "points"}
    </Label>
  );

  const menuItems = [
    {
      label: "Open conversation",
      cmd: "open",
      icon: FaEye,
      hidden: cancelled,
    },
    {
      label: "Reschedule",
      cmd: "reschedule",
      icon: FaCalendar,
      hidden: !actions.canReschedule,
    },
    {
      label: "Cancel",
      cmd: "cancel",
      icon: FaTrash,
      color: "danger",
      hidden: !actions.canCancel,
    },
  ].filter((x) => !x.hidden);

  const onMenu = (_: number, cmd: string | undefined) => {
    if (cmd === "open") history.push(playHref(convo));
    if (cmd === "reschedule") actions.startReschedule();
    if (cmd === "cancel") actions.startCancel();
  };

  return (
    <Row dim={cancelled}>
      {actions.modals}
      <DateBlock aria-hidden>
        <span>{format(date, "EEE")}</span>
        <span>{format(date, "d")}</span>
        <span>{format(date, "MMM")}</span>
      </DateBlock>
      <Main>
        <TitleLine>
          {cancelled ? (
            <strong>{convo.type}</strong>
          ) : (
            <Link to={playHref(convo)}>{convo.type}</Link>
          )}
          <span>
            {format(date, "EEE d MMM, HH:mm")} · {convo.duration} min
          </span>
        </TitleLine>
        <Meta>
          {isObserver ? (
            <span>
              <FaEye aria-hidden /> Observing {convo.manager.name} and{" "}
              {convo.directReport.name}
            </span>
          ) : (
            <span>
              <UserProfileImage
                name={other.name}
                profileImage={other.profileImage}
                size={24}
              />
              {other.name}
              {withManager ? " (your manager)" : ""}
            </span>
          )}
          {repeat && (
            <span>
              <MdRepeat aria-hidden /> {repeat}
            </span>
          )}
          {convo.location !== "" && (
            <span>
              <FaMapMarkerAlt aria-hidden /> {convo.location}
            </span>
          )}
          {convo.observers.length > 0 && !isObserver && (
            <span>
              <FaEye aria-hidden /> Observed by{" "}
              {convo.observers.map((o) => o.name).join(", ")}
            </span>
          )}
          {status}
        </Meta>
      </Main>
      <Actions>
        {upcoming && !cancelled ? (
          <Button small to={detailsHref(convo)}>
            Prepare
          </Button>
        ) : !cancelled ? (
          <Button small to={playHref(convo)}>
            Open
          </Button>
        ) : null}
        {menuItems.length > 0 && (
          <DotMenu items={menuItems} onItemClick={onMenu} />
        )}
      </Actions>
    </Row>
  );
};
